/**
 * Meta (Facebook) Pixel Tracking Utility
 * Handles pixel initialization and event tracking for Facebook Ads
 */

declare global {
  interface Window {
    fbq: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue: unknown[];
      loaded: boolean;
      version: string;
    };
    _fbq: typeof Window.prototype.fbq;
  }
}

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;

let isInitialized = false;

/**
 * Generate a unique event ID for deduplication
 */
export function generateEventId(): string {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Capture _fbc from fbclid URL param if not already set
 * This improves match rates significantly for India traffic
 */
function ensureFbcCookie(): void {
  try {
    const cookies = document.cookie;
    if (cookies.includes('_fbc=')) return; // Already set
    
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');
    
    if (fbclid) {
      const timestamp = Date.now();
      const fbc = `fb.1.${timestamp}.${fbclid}`;
      // Set cookie for 90 days
      const expires = new Date(timestamp + 90 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `_fbc=${fbc}; expires=${expires}; path=/; SameSite=Lax`;
      console.log('Set _fbc cookie from fbclid');
    }
  } catch (e) {
    console.warn('Could not set _fbc cookie:', e);
  }
}

/**
 * Initialize the Meta Pixel
 * Should be called once on app load
 */
export function initMetaPixel(): void {
  if (isInitialized || !PIXEL_ID) {
    if (!PIXEL_ID) {
      console.warn('Meta Pixel ID not configured');
    }
    return;
  }

  // Ensure _fbc cookie is set from fbclid
  ensureFbcCookie();

  // Meta Pixel base code
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  window.fbq = function(...args: unknown[]) {
    if (window.fbq.callMethod) {
      window.fbq.callMethod(...args);
    } else {
      window.fbq.queue.push(args);
    }
  } as Window['fbq'];
  
  window.fbq.queue = [];
  window.fbq.loaded = true;
  window.fbq.version = '2.0';
  window._fbq = window.fbq;

  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
  
  isInitialized = true;
  console.log('Meta Pixel initialized');
}

/**
 * Track a standard Meta Pixel event with optional eventID for deduplication
 */
export function trackPixelEvent(
  eventName: string,
  params?: Record<string, unknown>,
  options?: { eventID?: string }
): void {
  if (!isInitialized || !window.fbq) {
    console.warn('Meta Pixel not initialized');
    return;
  }
  
  const eventParams = params || {};
  const eventOptions = options?.eventID ? { eventID: options.eventID } : {};
  
  window.fbq('track', eventName, eventParams, eventOptions);
}

/**
 * Track a custom Meta Pixel event with optional eventID for deduplication
 */
export function trackCustomPixelEvent(
  eventName: string,
  params?: Record<string, unknown>,
  options?: { eventID?: string }
): void {
  if (!isInitialized || !window.fbq) {
    console.warn('Meta Pixel not initialized');
    return;
  }
  
  const eventParams = params || {};
  const eventOptions = options?.eventID ? { eventID: options.eventID } : {};
  
  window.fbq('trackCustom', eventName, eventParams, eventOptions);
}

/**
 * Pre-built event trackers for common conversions
 * All methods support eventID for deduplication with CAPI
 */
export const metaPixel = {
  pageView: () => trackPixelEvent('PageView'),
  
  lead: (
    params?: { content_name?: string; content_category?: string; value?: number; currency?: string },
    options?: { eventID?: string }
  ) => trackPixelEvent('Lead', params, options),
  
  completeRegistration: (
    params?: { content_name?: string; status?: string; value?: number; currency?: string },
    options?: { eventID?: string }
  ) => trackPixelEvent('CompleteRegistration', params, options),
  
  contact: (
    params?: { content_name?: string },
    options?: { eventID?: string }
  ) => trackPixelEvent('Contact', params, options),
  
  viewContent: (
    params?: { content_name?: string; content_category?: string; content_ids?: string[]; content_type?: string; value?: number; currency?: string },
    options?: { eventID?: string }
  ) => trackPixelEvent('ViewContent', params, options),
  
  schedule: (options?: { eventID?: string }) => trackPixelEvent('Schedule', undefined, options),
  
  // Custom events for real estate
  brochureDownload: (
    params?: { content_name?: string },
    options?: { eventID?: string }
  ) => trackCustomPixelEvent('BrochureDownload', params, options),
  
  floorPlanView: (
    params?: { unit_type?: string },
    options?: { eventID?: string }
  ) => trackCustomPixelEvent('FloorPlanView', params, options),
  
  virtualTourStart: (options?: { eventID?: string }) => 
    trackCustomPixelEvent('VirtualTourStart', undefined, options),
  
  whatsAppClick: (
    params?: { page_path?: string },
    options?: { eventID?: string }
  ) => trackPixelEvent('Contact', params, options), // Use standard Contact event
  
  priceEnquiry: (options?: { eventID?: string }) => 
    trackCustomPixelEvent('PriceEnquiry', undefined, options),
  
  siteVisitRequest: (options?: { eventID?: string }) => 
    trackCustomPixelEvent('SiteVisitRequest', undefined, options),
};
